import { CustomizedButton, CustomizedFormErrorMessage, CustomizedModalBase } from 'components/common/index';
import classes from 'components/common/ProgramLibraryDropdownMenu/components/RenameSubProgramModal/RenameSubProgramModal.module.scss';
import { DEFAULT_ERROR, useCustomSnackBar } from 'hooks/useCustomSnackBar';
import { Controller, useForm } from 'react-hook-form';
import TextSearchInput from 'components/common/TextSearchInput/textSearchInput';
import { useProjectTreeStore } from 'store/projectContract.store';
import { useMutationRenameSubProgram } from 'api/mutations/renameSubProgram.mutation';

interface Props {
    showModal: boolean;
    onHideModal: () => void;
}

interface IProjectName {
    contractName: string;
}

export const RenameSubProgramModal = ({ showModal, onHideModal }: Props) => {
    const { contract, update } = useProjectTreeStore();

    const { handleSnackBar } = useCustomSnackBar();
    const { mutate, isLoading } = useMutationRenameSubProgram();

    const {
        handleSubmit,
        control,
        formState: { errors },
        setValue,
        reset,
    } = useForm<IProjectName>({
        values: {
            contractName: contract ? contract?.title : '',
        },
    });

    const handleSave = (value: IProjectName) => {
        if (!contract) {
            return;
        }
        mutate(
            {
                contractName: value.contractName,
                contractId: contract.id,
            },
            {
                onSuccess: () => {
                    handleSnackBar('Successfully renamed!', 'success');
                    update({ contract: { ...contract, title: value.contractName } });
                },
                onError: (error) => {
                    DEFAULT_ERROR({ error, handleError: handleSnackBar });
                },
                onSettled: () => {
                    closeModal();
                },
            },
        );
    };

    const closeModal = () => {
        onHideModal();
        reset();
    };

    return (
        <CustomizedModalBase isModalOpen={showModal} handleModalClose={closeModal}>
            <form onSubmit={handleSubmit(handleSave)} autoComplete="off">
                <div className={classes.content}>
                    <div className={classes.title}>Sub Program Rename</div>
                    <div className={classes.subTitle}>Current name: {contract?.title}</div>
                    <div className={classes.inputWrapper}>
                        <label>New name</label>
                        <Controller
                            render={({ field }) => (
                                <TextSearchInput
                                    {...field}
                                    id={'contractName'}
                                    isSearch={false}
                                    className={classes.input}
                                    placeholder="Enter sub-program name..."
                                    maxLength={30}
                                    handleClear={() => {
                                        setValue('contractName', '');
                                    }}
                                />
                            )}
                            name="contractName"
                            control={control}
                            rules={{
                                validate: {
                                    min: (value) => (value && value.length >= 2) || 'must be at least 2 characters',
                                    notValidName: (value) =>
                                        (value && value.toLowerCase() !== 'all') ||
                                        '"All" is not a valid sub-program name.',
                                },
                            }}
                        />
                        {errors.contractName && <CustomizedFormErrorMessage text={errors.contractName.message} />}
                    </div>

                    <div className={classes.footer}>
                        <div className={classes.container}>
                            <CustomizedButton size={'large'} color={'secondary'} onClick={closeModal}>
                                Cancel
                            </CustomizedButton>

                            <CustomizedButton
                                type={'submit'}
                                size={'large'}
                                color={'primary'}
                                isLoading={isLoading}
                                disabled={isLoading}
                            >
                                Rename
                            </CustomizedButton>
                        </div>
                    </div>
                </div>
            </form>
        </CustomizedModalBase>
    );
};
